import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useTranslation } from "react-i18next";
import { FacebookCommentModerationOptions } from "store/platforms/types";
import { RadioGroup } from "components/inputs/radio-group";
import styles from "./index.module.scss";
import { Toggle, ToggleCard } from "components/inputs/toggle/Toggle";

interface CommentModerationOptionsProps {
    selectedRadioCommentModerationOption: FacebookCommentModerationOptions;
    setSelectedRadioCommentModerationOption: (
        option: FacebookCommentModerationOptions
    ) => void;
    hasToggledDiscussion: boolean;
    setHasToggledDiscussion: (value: boolean) => void;
}

export const CommentModerationOptions = ({
    selectedRadioCommentModerationOption,
    setSelectedRadioCommentModerationOption,
    hasToggledDiscussion,
    setHasToggledDiscussion
}: CommentModerationOptionsProps) => {
    const { t } = useTranslation();
    const { commentModerationOptions } = useSelector(
        (state: RootState) => state.platforms.facebook
    );

    const options = useMemo(
        () => [
            {
                id: "DEFAULT",
                name: "Default",
                description: "All viewers can participate in chat."
            },
            ...commentModerationOptions.filter((o) => !(o.id === "DISCUSSION"))
        ],
        [commentModerationOptions]
    );

    return (
        <div className={styles["comment-moderation-options"]}>
            <RadioGroup
                selected={selectedRadioCommentModerationOption.id}
                title={t("facebook-destination:comment-moderation")}
                onChange={(value) =>
                    setSelectedRadioCommentModerationOption(
                        options.find((option) => option.id === value)
                    )
                }
                options={options.map((option) => ({
                    id: option.id,
                    label: (
                        <ToggleCard
                            title={option.name}
                            desc={option.description}
                        />
                    )
                }))}
            />
            {commentModerationOptions
                .filter((o) => o.id === "DISCUSSION")
                .map((option) => (
                    <Toggle
                        key={option.id}
                        id={option.id}
                        label={
                            <ToggleCard
                                title={option.name}
                                desc={option.description}
                            />
                        }
                        onToggle={() =>
                            setHasToggledDiscussion(!hasToggledDiscussion)
                        }
                        on={hasToggledDiscussion}
                    />
                ))}
        </div>
    );
};
